// Modified Triadan dentitions + AVDC finding symbols + charting scenarios.
// Quadrants: 1 = upper right, 2 = upper left, 3 = lower left, 4 = lower right.
// Last two Triadan digits encode tooth position/type.

const TYPE_BY_POS = {
  1: "I1",
  2: "I2",
  3: "I3",
  4: "C",
  5: "P1",
  6: "P2",
  7: "P3",
  8: "P4",
  9: "M1",
  10: "M2",
  11: "M3",
};

export function toothType(id) {
  return TYPE_BY_POS[id % 100] || "—";
}

// Per quadrant arrays are ordered for left→right screen layout:
// upper-right & lower-right run caudal→midline; upper-left & lower-left run midline→caudal.
export const DENTITION = {
  dog: {
    teeth: 42,
    UR: [110, 109, 108, 107, 106, 105, 104, 103, 102, 101],
    UL: [201, 202, 203, 204, 205, 206, 207, 208, 209, 210],
    LR: [411, 410, 409, 408, 407, 406, 405, 404, 403, 402, 401],
    LL: [301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311],
  },
  cat: {
    teeth: 30,
    UR: [109, 108, 107, 106, 104, 103, 102, 101],
    UL: [201, 202, 203, 204, 206, 207, 208, 209],
    LR: [409, 408, 407, 404, 403, 402, 401],
    LL: [301, 302, 303, 304, 307, 308, 309],
  },
};

export const SYMBOLS = [
  { code: "X", name: "Extraction", desc: "Tooth requires extraction", color: "#dc2626" },
  { code: "F", name: "Fracture", desc: "Crown / root fracture", color: "#ea580c" },
  { code: "RL", name: "Resorptive lesion", desc: "Tooth resorption (common in cats)", color: "#c026d3" },
  { code: "CA", name: "Calculus", desc: "Heavy calculus / tartar", color: "#ca8a04" },
  { code: "GR", name: "Gingival recession", desc: "Recession exposing root", color: "#0d9488" },
  { code: "Mo", name: "Mobility", desc: "Mobile / loose tooth", color: "#7c3aed" },
  { code: "PD", name: "Periodontitis", desc: "Periodontal disease (grade 3–4)", color: "#b45309" },
  { code: "M", name: "Missing", desc: "Tooth absent / lost", color: "#64748b" },
  { code: "C", name: "Caries", desc: "Dental caries / decay", color: "#0369a1" },
];

export const SYMBOL_BY_CODE = Object.fromEntries(SYMBOLS.map((s) => [s.code, s]));

// Each finding: { tooth (Triadan), symbols (expected AVDC codes), note (briefing text) }
export const DENTAL_CASES = [
  {
    id: "bella_perio",
    name: "Bella",
    species: "dog",
    breed: "Yorkshire Terrier",
    age: "11 yr",
    title: "Periodontal Disease",
    difficulty: "Beginner",
    briefing:
      "Small-breed senior dog presented for COHAT with grade III periodontal disease, marked halitosis and several missing teeth.",
    findings: [
      { tooth: 104, symbols: ["CA", "PD"], note: "Upper right canine — heavy calculus with grade III periodontitis." },
      { tooth: 108, symbols: ["CA"], note: "Upper right 4th premolar (carnassial) — heavy calculus." },
      { tooth: 209, symbols: ["PD"], note: "Upper left 1st molar — periodontitis, grade 3." },
      { tooth: 304, symbols: ["Mo"], note: "Lower left canine — grade 2 mobility." },
      { tooth: 407, symbols: ["M"], note: "Lower right 3rd premolar — missing." },
      { tooth: 301, symbols: ["M"], note: "Lower left central incisor — missing." },
    ],
  },
  {
    id: "rocky_fracture",
    name: "Rocky",
    species: "dog",
    breed: "Labrador Retriever",
    age: "4 yr",
    title: "Slab Fracture",
    difficulty: "Beginner",
    briefing:
      "Active dog with a bone-chewing habit. Complicated crown fracture with pulp exposure on a carnassial tooth.",
    findings: [
      { tooth: 108, symbols: ["F", "X"], note: "Upper right 4th premolar (carnassial) — slab fracture with pulp exposure; extract." },
      { tooth: 208, symbols: ["F"], note: "Upper left 4th premolar — uncomplicated enamel fracture." },
      { tooth: 404, symbols: ["F"], note: "Lower right canine — crown fracture." },
      { tooth: 104, symbols: ["CA"], note: "Upper right canine — mild calculus." },
    ],
  },
  {
    id: "whiskey_resorption",
    name: "Whiskey",
    species: "cat",
    breed: "Domestic Shorthair",
    age: "9 yr",
    title: "Resorptive Lesions",
    difficulty: "Intermediate",
    briefing:
      "Senior cat presented for COHAT after picky eating. Multiple painful resorptive lesions — apply BOTH the lesion and the treatment decision.",
    findings: [
      { tooth: 304, symbols: ["RL", "X"], note: "Lower left canine — resorptive lesion at neck of tooth; extract." },
      { tooth: 404, symbols: ["RL", "X"], note: "Lower right canine — mirror-image resorption; extract." },
      { tooth: 307, symbols: ["RL", "X"], note: "Lower left 3rd premolar — advanced resorption; extract." },
      { tooth: 407, symbols: ["RL", "X"], note: "Lower right 3rd premolar — same lesion; extract." },
      { tooth: 108, symbols: ["CA"], note: "Upper right 4th premolar (carnassial) — heavy calculus." },
    ],
  },
  {
    id: "mochi_gingivitis",
    name: "Mochi",
    species: "cat",
    breed: "Maine Coon",
    age: "2 yr",
    title: "Juvenile Gingivitis",
    difficulty: "Beginner",
    briefing:
      "Young cat with diffuse marginal gingivitis and early calculus. Routine clean and chart the affected teeth.",
    findings: [
      { tooth: 108, symbols: ["CA"], note: "Upper right 4th premolar — calculus with marginal gingivitis." },
      { tooth: 208, symbols: ["CA"], note: "Upper left 4th premolar — calculus." },
      { tooth: 409, symbols: ["CA"], note: "Lower right 1st molar — calculus." },
      { tooth: 309, symbols: ["CA"], note: "Lower left 1st molar — calculus." },
    ],
  },
  {
    id: "pierre_multiquadrant",
    name: "Pierre",
    species: "dog",
    breed: "French Bulldog",
    age: "8 yr",
    title: "Multi-quadrant Disease",
    difficulty: "Advanced",
    briefing:
      "Brachycephalic with chronic dental crowding and rotation. Multiple extractions, fractures and advanced periodontitis across all quadrants.",
    findings: [
      { tooth: 105, symbols: ["X"], note: "Upper right 1st premolar — crowded/rotated; extract." },
      { tooth: 106, symbols: ["X"], note: "Upper right 2nd premolar — crowded; extract." },
      { tooth: 108, symbols: ["F", "X"], note: "Upper right 4th premolar — fractured; extract." },
      { tooth: 204, symbols: ["PD", "Mo"], note: "Upper left canine — periodontitis with grade 2 mobility." },
      { tooth: 409, symbols: ["PD"], note: "Lower right 1st molar — periodontitis, grade 4." },
      { tooth: 301, symbols: ["M"], note: "Lower left central incisor — missing." },
      { tooth: 311, symbols: ["M"], note: "Lower left 3rd molar — missing." },
    ],
  },
  {
    id: "coco_onf",
    name: "Coco",
    species: "dog",
    breed: "Dachshund",
    age: "12 yr",
    title: "Oronasal Fistula",
    difficulty: "Advanced",
    briefing:
      "Geriatric Dachshund with chronic sneezing and nasal discharge. Oronasal fistula suspected at a previous extraction site; surrounding teeth deteriorating. Multi-finding teeth — apply ALL relevant symbols.",
    findings: [
      { tooth: 204, symbols: ["PD", "GR", "X"], note: "Upper left canine — deep periodontal pocket with recession and oronasal fistula; extract." },
      { tooth: 206, symbols: ["M"], note: "Upper left 2nd premolar — previously extracted (ONF site); missing." },
      { tooth: 208, symbols: ["PD", "Mo"], note: "Upper left 4th premolar — periodontitis with mobility." },
      { tooth: 104, symbols: ["CA", "GR"], note: "Upper right canine — calculus with gingival recession." },
      { tooth: 110, symbols: ["M"], note: "Upper right 2nd molar — missing." },
      { tooth: 209, symbols: ["PD"], note: "Upper left 1st molar — periodontitis, grade 3." },
    ],
  },
  {
    id: "duke_cohat",
    name: "Duke",
    species: "dog",
    breed: "German Shepherd",
    age: "9 yr",
    title: "COHAT Periodontal Mapping",
    difficulty: "Advanced",
    cohat: true,
    briefing:
      "Full COHAT under general anaesthesia. Chart the AVDC findings AND record a periodontal exam on the affected teeth — pocket depth (mm) and gingivitis (GI) index.",
    findings: [
      { tooth: 104, symbols: ["CA", "PD"], note: "Upper right canine — calculus with periodontitis." },
      { tooth: 108, symbols: ["CA"], note: "Upper right 4th premolar (carnassial) — heavy calculus." },
      { tooth: 208, symbols: ["PD"], note: "Upper left 4th premolar — periodontitis." },
      { tooth: 409, symbols: ["PD"], note: "Lower right 1st molar — advanced periodontitis." },
      { tooth: 304, symbols: ["GR"], note: "Lower left canine — gingival recession." },
    ],
    perio: [
      { tooth: 104, pocket: 6, gi: 3, note: "Upper right canine — 6 mm pocket, GI 3." },
      { tooth: 108, pocket: 5, gi: 2, note: "Carnassial — 5 mm pocket, GI 2." },
      { tooth: 208, pocket: 7, gi: 3, note: "Upper left carnassial — 7 mm pocket, GI 3." },
      { tooth: 409, pocket: 8, gi: 3, note: "Lower right M1 — 8 mm pocket, GI 3." },
      { tooth: 304, pocket: 4, gi: 2, note: "Lower left canine — 4 mm pocket, GI 2." },
    ],
  },
  {
    id: "luna_feline_perio",
    name: "Luna",
    species: "cat",
    breed: "British Shorthair",
    age: "7 yr",
    title: "Feline Periodontal Probing",
    difficulty: "Intermediate",
    cohat: true,
    briefing:
      "Feline COHAT. Alongside the AVDC findings, probe the affected teeth and record pocket depth (mm) and gingivitis (GI) index.",
    findings: [
      { tooth: 104, symbols: ["CA"], note: "Upper right canine — calculus." },
      { tooth: 108, symbols: ["GR"], note: "Upper right 4th premolar — gingival recession." },
      { tooth: 209, symbols: ["PD"], note: "Upper left 1st molar — periodontitis." },
      { tooth: 409, symbols: ["PD"], note: "Lower right 1st molar — periodontitis." },
      { tooth: 307, symbols: ["RL", "X"], note: "Lower left 3rd premolar — resorptive lesion; extract." },
    ],
    perio: [
      { tooth: 104, pocket: 4, gi: 2, note: "Upper right canine — 4 mm pocket, GI 2." },
      { tooth: 108, pocket: 5, gi: 3, note: "Upper right carnassial — 5 mm pocket, GI 3." },
      { tooth: 209, pocket: 4, gi: 2, note: "Upper left M1 — 4 mm pocket, GI 2." },
      { tooth: 409, pocket: 5, gi: 3, note: "Lower right M1 — 5 mm pocket, GI 3." },
      { tooth: 307, pocket: 3, gi: 2, note: "Lower left P3 — 3 mm pocket, GI 2." },
    ],
  },
  {
    id: "ginger_stomatitis",
    name: "Ginger",
    species: "cat",
    breed: "Domestic Shorthair",
    age: "6 yr",
    title: "Feline Chronic Gingivostomatitis",
    difficulty: "Advanced",
    briefing:
      "Cat with severe oral pain, dysphagia and drooling. Caudal stomatitis with widespread inflammation; the plan is full-mouth/caudal extractions. Apply BOTH the disease and the extraction decision.",
    findings: [
      { tooth: 108, symbols: ["PD", "X"], note: "Upper right 4th premolar — severe periodontitis at the inflamed caudal mouth; extract." },
      { tooth: 109, symbols: ["PD", "X"], note: "Upper right 1st molar — caudal stomatitis; extract." },
      { tooth: 208, symbols: ["PD", "X"], note: "Upper left 4th premolar — mirror disease; extract." },
      { tooth: 409, symbols: ["PD", "X"], note: "Lower right 1st molar — severe inflammation; extract." },
      { tooth: 309, symbols: ["PD", "X"], note: "Lower left 1st molar — severe inflammation; extract." },
      { tooth: 104, symbols: ["CA"], note: "Upper right canine — calculus." },
    ],
  },
  {
    id: "buster_canine_fracture",
    name: "Buster",
    species: "dog",
    breed: "Belgian Malinois",
    age: "3 yr",
    title: "Complicated Canine Fracture",
    difficulty: "Intermediate",
    briefing:
      "Working dog that fractured a canine tooth on a cage door. Complicated crown fracture with pulp exposure; the tooth needs extraction or referral for root canal.",
    findings: [
      { tooth: 204, symbols: ["F", "X"], note: "Upper left canine — complicated crown fracture with pulp exposure; extract (or refer for endodontics)." },
      { tooth: 104, symbols: ["F"], note: "Upper right canine — uncomplicated chip fracture, no pulp exposure." },
      { tooth: 108, symbols: ["CA"], note: "Upper right 4th premolar — mild calculus." },
      { tooth: 304, symbols: ["CA"], note: "Lower left canine — mild calculus." },
    ],
  },
  {
    id: "poppy_crowding",
    name: "Poppy",
    species: "dog",
    breed: "Pomeranian",
    age: "1 yr",
    title: "Toy-breed Crowding & Persistent Deciduous",
    difficulty: "Intermediate",
    briefing:
      "Young toy breed with crowded, rotated premolars and a persistent deciduous canine causing malocclusion. Chart the crowded teeth needing extraction.",
    findings: [
      { tooth: 305, symbols: ["X"], note: "Lower left 1st premolar — crowded/rotated; extract." },
      { tooth: 405, symbols: ["X"], note: "Lower right 1st premolar — crowded/rotated; extract." },
      { tooth: 304, symbols: ["GR", "X"], note: "Lower left canine — persistent deciduous crowding with recession; extract the retained tooth." },
      { tooth: 106, symbols: ["CA"], note: "Upper right 2nd premolar — early calculus." },
    ],
  },
  {
    id: "smokey_rl_advanced",
    name: "Smokey",
    species: "cat",
    breed: "Burmese",
    age: "11 yr",
    title: "Advanced Tooth Resorption",
    difficulty: "Advanced",
    briefing:
      "Senior cat that stopped eating dry food and chatters its jaw when touched. Multiple advanced resorptive lesions across the mouth. Apply BOTH the lesion and the extraction decision.",
    findings: [
      { tooth: 307, symbols: ["RL", "X"], note: "Lower left 3rd premolar — advanced resorption; extract." },
      { tooth: 407, symbols: ["RL", "X"], note: "Lower right 3rd premolar — advanced resorption; extract." },
      { tooth: 108, symbols: ["RL", "X"], note: "Upper right 4th premolar — resorptive lesion; extract." },
      { tooth: 208, symbols: ["RL", "X"], note: "Upper left 4th premolar — resorptive lesion; extract." },
      { tooth: 304, symbols: ["Mo"], note: "Lower left canine — grade 2 mobility." },
    ],
  },
  {
    id: "rex_molar_perio",
    name: "Rex",
    species: "dog",
    breed: "Rottweiler",
    age: "8 yr",
    title: "Carnassial & Molar Periodontitis",
    difficulty: "Intermediate",
    briefing:
      "Large breed with deep pockets around the caudal teeth and a facial swelling under the eye. Advanced periodontitis of the carnassial and first molar.",
    findings: [
      { tooth: 108, symbols: ["PD", "X"], note: "Upper right 4th premolar (carnassial) — deep pocket with a draining tract; extract." },
      { tooth: 109, symbols: ["PD"], note: "Upper right 1st molar — periodontitis, grade 3." },
      { tooth: 409, symbols: ["PD", "Mo"], note: "Lower right 1st molar — periodontitis with grade 2 mobility." },
      { tooth: 104, symbols: ["CA"], note: "Upper right canine — heavy calculus." },
      { tooth: 204, symbols: ["CA"], note: "Upper left canine — heavy calculus." },
    ],
  },
  {
    id: "maisie_caries",
    name: "Maisie",
    species: "dog",
    breed: "Cavalier King Charles Spaniel",
    age: "7 yr",
    title: "Dental Caries",
    difficulty: "Advanced",
    briefing:
      "Dog with a dark, soft cavity on the chewing surface of a maxillary molar — true caries (uncommon in dogs). Chart the decay and surrounding disease.",
    findings: [
      { tooth: 109, symbols: ["C"], note: "Upper right 1st molar — occlusal caries (decay) on the chewing surface." },
      { tooth: 110, symbols: ["C", "X"], note: "Upper right 2nd molar — advanced caries undermining the crown; extract." },
      { tooth: 108, symbols: ["CA"], note: "Upper right 4th premolar — calculus." },
      { tooth: 209, symbols: ["CA"], note: "Upper left 1st molar — calculus." },
    ],
  },
  {
    id: "felix_feline_cohat",
    name: "Felix",
    species: "cat",
    breed: "Ragdoll",
    age: "8 yr",
    title: "Feline COHAT with Probing",
    difficulty: "Advanced",
    cohat: true,
    briefing:
      "Feline COHAT under GA. Alongside the AVDC findings, probe the affected teeth and record pocket depth (mm) and gingivitis (GI) index.",
    findings: [
      { tooth: 104, symbols: ["CA", "GR"], note: "Upper right canine — calculus with recession." },
      { tooth: 108, symbols: ["PD"], note: "Upper right 4th premolar — periodontitis." },
      { tooth: 208, symbols: ["PD"], note: "Upper left 4th premolar — periodontitis." },
      { tooth: 409, symbols: ["RL", "X"], note: "Lower right 1st molar — resorptive lesion; extract." },
      { tooth: 304, symbols: ["Mo"], note: "Lower left canine — grade 1 mobility." },
    ],
    perio: [
      { tooth: 104, pocket: 3, gi: 2, note: "Upper right canine — 3 mm pocket, GI 2." },
      { tooth: 108, pocket: 5, gi: 3, note: "Upper right carnassial — 5 mm pocket, GI 3." },
      { tooth: 208, pocket: 4, gi: 3, note: "Upper left carnassial — 4 mm pocket, GI 3." },
      { tooth: 409, pocket: 4, gi: 2, note: "Lower right M1 — 4 mm pocket, GI 2." },
      { tooth: 304, pocket: 3, gi: 1, note: "Lower left canine — 3 mm pocket, GI 1." },
    ],
  },
  {
    id: "tank_mastiff_cohat",
    name: "Tank",
    species: "dog",
    breed: "Bullmastiff",
    age: "10 yr",
    title: "Geriatric COHAT Mapping",
    difficulty: "Advanced",
    cohat: true,
    briefing:
      "Geriatric large breed for a full COHAT under GA. Chart the AVDC findings AND record the periodontal exam — pocket depth (mm) and gingivitis (GI) index — on the affected teeth.",
    findings: [
      { tooth: 104, symbols: ["CA", "PD"], note: "Upper right canine — calculus with periodontitis." },
      { tooth: 108, symbols: ["PD", "Mo"], note: "Upper right 4th premolar — periodontitis with mobility." },
      { tooth: 208, symbols: ["F"], note: "Upper left 4th premolar — slab fracture, no pulp exposure." },
      { tooth: 409, symbols: ["PD", "X"], note: "Lower right 1st molar — grade 4 periodontitis; extract." },
      { tooth: 309, symbols: ["GR"], note: "Lower left 1st molar — gingival recession." },
    ],
    perio: [
      { tooth: 104, pocket: 5, gi: 2, note: "Upper right canine — 5 mm pocket, GI 2." },
      { tooth: 108, pocket: 7, gi: 3, note: "Upper right carnassial — 7 mm pocket, GI 3." },
      { tooth: 208, pocket: 3, gi: 1, note: "Upper left carnassial — 3 mm pocket, GI 1." },
      { tooth: 409, pocket: 9, gi: 3, note: "Lower right M1 — 9 mm pocket, GI 3." },
      { tooth: 309, pocket: 4, gi: 2, note: "Lower left M1 — 4 mm pocket, GI 2." },
    ],
  },
];

// Short badge for a tooth's periodontal readings, e.g. "5mm G3".
export function perioSummary(p) {
  if (!p) return "";
  const parts = [];
  if (p.pocket != null) parts.push(`${p.pocket}mm`);
  if (p.gi) parts.push(`G${p.gi}`);
  return parts.join(" ");
}

// ---- Anatomical arcade (occlusal-view dental chart) ----
// Shared by the on-screen SVG (ToothArcade) and the PDF export. Modeled on the
// classic occlusal veterinary dental chart: a wide horseshoe maxillary arcade on
// top (incisors at the apex, large curved canines at the corners, premolars &
// molars curving down/in along the sides) and a narrower mandibular arcade below.
// Teeth are organic occlusal outlines, rotated so their long (mesiodistal) axis
// follows the arch. Patient's right is on screen-left.
export const ARCADE_VIEW = { w: 900, h: 1000, midX: 450 };

const MAX_ARCH = { cx: 450, cy: 380, a: 182, b: 290, tmax: 1.92 };
const MAND_ARCH = { cx: 450, cy: 655, a: 172, b: 262, tmax: 1.92 };

function archFor(jaw) {
  return jaw === "max" ? MAX_ARCH : MAND_ARCH;
}

// occlusal silhouette family + size (W = bucco-lingual, L = mesiodistal) per type
function toothMeta(type, jaw) {
  if (type === "C") return { shape: "canine", W: 30, L: 66 };
  if (type.startsWith("I")) return { shape: "incisor", W: 15, L: 22 };
  if (type === "P4") return { shape: "carnassial", W: 34, L: 74 };
  if (type === "M1" && jaw === "mand") return { shape: "carnassial", W: 36, L: 62 };
  if (type.startsWith("M")) return { shape: "molar", W: 30, L: 34 };
  if (type === "P3") return { shape: "premolar", W: 18, L: 44 };
  if (type.startsWith("P")) return { shape: "premolar", W: 15, L: 30 };
  return { shape: "premolar", W: 15, L: 28 };
}

export function arcadeLayout(species) {
  const dent = DENTITION[species];
  const quads = [
    { teeth: [...dent.UR].reverse(), side: "right", jaw: "max" },
    { teeth: dent.UL, side: "left", jaw: "max" },
    { teeth: [...dent.LR].reverse(), side: "right", jaw: "mand" },
    { teeth: dent.LL, side: "left", jaw: "mand" },
  ];
  const out = [];
  quads.forEach(({ teeth, side, jaw }) => {
    const N = teeth.length;
    const E = archFor(jaw);
    const sgn = side === "right" ? -1 : 1;
    teeth.forEach((id, i) => {
      const t = ((i + 0.55) / N) * E.tmax;
      const x = E.cx + sgn * E.a * Math.sin(t);
      const y = jaw === "max" ? E.cy - E.b * Math.cos(t) : E.cy + E.b * Math.cos(t);
      // arch tangent → long axis (mesiodistal) of the tooth
      const dx = sgn * E.a * Math.cos(t);
      const dy = jaw === "max" ? E.b * Math.sin(t) : -E.b * Math.sin(t);
      const rot = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
      const type = toothType(id);
      out.push({ id, type, jaw, side, x, y, rot, ...toothMeta(type, jaw) });
    });
  });
  return out;
}

// outward (away from arch centre) direction for label placement
export function arcadeOutward(tt) {
  const E = archFor(tt.jaw);
  const dx = tt.x - E.cx;
  const dy = tt.y - E.cy;
  const len = Math.hypot(dx, dy) || 1;
  return [dx / len, dy / len];
}

// Organic occlusal outline as cubic-bezier data in the tooth's local frame
// (centred at 0,0, long axis vertical). One source of truth for SVG + PDF.
function shapeDef(shape, W, L) {
  const w = W;
  const l = L;
  switch (shape) {
    case "incisor":
      return { start: [0, -l * 0.5], segs: [
        [w * 0.5, -l * 0.3, w * 0.45, l * 0.32, 0, l * 0.5],
        [-w * 0.45, l * 0.32, -w * 0.5, -l * 0.3, 0, -l * 0.5],
      ] };
    case "canine":
      return { start: [0, -l * 0.5], segs: [
        [w * 0.42, -l * 0.16, w * 0.52, l * 0.22, w * 0.26, l * 0.42],
        [w * 0.12, l * 0.53, -w * 0.12, l * 0.53, -w * 0.26, l * 0.42],
        [-w * 0.52, l * 0.22, -w * 0.42, -l * 0.16, 0, -l * 0.5],
      ] };
    case "premolar":
      return { start: [0, -l * 0.5], segs: [
        [w * 0.52, -l * 0.26, w * 0.46, l * 0.2, w * 0.18, l * 0.46],
        [w * 0.06, l * 0.55, -w * 0.06, l * 0.55, -w * 0.18, l * 0.46],
        [-w * 0.46, l * 0.2, -w * 0.52, -l * 0.26, 0, -l * 0.5],
      ] };
    case "carnassial":
      return { start: [0, -l * 0.5], segs: [
        [w * 0.5, -l * 0.42, w * 0.56, -l * 0.08, w * 0.42, l * 0.14],
        [w * 0.34, l * 0.3, w * 0.48, l * 0.42, w * 0.22, l * 0.5],
        [w * 0.04, l * 0.56, -w * 0.2, l * 0.5, -w * 0.36, l * 0.34],
        [-w * 0.56, l * 0.1, -w * 0.5, -l * 0.4, 0, -l * 0.5],
      ] };
    case "molar":
    default:
      return { start: [0, -l * 0.5], segs: [
        [w * 0.56, -l * 0.36, w * 0.58, l * 0.14, w * 0.4, l * 0.42],
        [w * 0.2, l * 0.6, -w * 0.2, l * 0.6, -w * 0.4, l * 0.42],
        [-w * 0.58, l * 0.14, -w * 0.56, -l * 0.36, 0, -l * 0.5],
      ] };
  }
}

export function shapePath(shape, W, L) {
  const d = shapeDef(shape, W, L);
  let p = `M ${d.start[0].toFixed(1)},${d.start[1].toFixed(1)}`;
  d.segs.forEach((s) => {
    p += ` C ${s[0].toFixed(1)},${s[1].toFixed(1)} ${s[2].toFixed(1)},${s[3].toFixed(1)} ${s[4].toFixed(1)},${s[5].toFixed(1)}`;
  });
  return p + " Z";
}

// sample the outline into a polyline (used by the PDF renderer)
export function shapeSamples(shape, W, L, steps = 7) {
  const d = shapeDef(shape, W, L);
  const pts = [d.start];
  let cur = d.start;
  d.segs.forEach((s) => {
    const c1 = [s[0], s[1]];
    const c2 = [s[2], s[3]];
    const p3 = [s[4], s[5]];
    for (let k = 1; k <= steps; k++) {
      const u = k / steps;
      const mt = 1 - u;
      const x = mt * mt * mt * cur[0] + 3 * mt * mt * u * c1[0] + 3 * mt * u * u * c2[0] + u * u * u * p3[0];
      const y = mt * mt * mt * cur[1] + 3 * mt * mt * u * c1[1] + 3 * mt * u * u * c2[1] + u * u * u * p3[1];
      pts.push([x, y]);
    }
    cur = p3;
  });
  return pts;
}

// faint internal cusp/fissure marks for character
export function shapeCusps(shape, W, L) {
  const w = W;
  const l = L;
  switch (shape) {
    case "premolar":
      return [[0, -l * 0.2, 0, l * 0.22]];
    case "carnassial":
      return [[0, -l * 0.32, 0, l * 0.28], [-w * 0.18, -l * 0.04, w * 0.16, l * 0.1]];
    case "molar":
      return [[-w * 0.2, -l * 0.04, w * 0.18, l * 0.12], [w * 0.04, -l * 0.18, -w * 0.08, l * 0.16]];
    default:
      return [];
  }
}

const letterFor = (pct) =>
  pct >= 90 ? "A" : pct >= 80 ? "B" : pct >= 70 ? "C" : pct >= 60 ? "D" : "F";

// Grade one periodontal reading against the expected value.
// Pocket depth is correct within ±1 mm; indices must match exactly.
function gradePerio(dentalCase, perioChart = {}) {
  const perio = dentalCase.perio || [];
  if (!perio.length) return { perioResult: [], perioPct: null };

  let total = 0;
  let correct = 0;
  const perioResult = perio.map((p) => {
    const got = perioChart[p.tooth] || {};
    const fields = [
      { key: "pocket", label: "PD (mm)", exp: p.pocket, val: got.pocket ?? null, ok: got.pocket != null && Math.abs(got.pocket - p.pocket) <= 1 },
      { key: "gi", label: "GI", exp: p.gi, val: got.gi ?? null, ok: got.gi === p.gi },
    ];
    const c = fields.filter((f) => f.ok).length;
    total += fields.length;
    correct += c;
    return { tooth: p.tooth, note: p.note, fields, correctCount: c, satisfied: c === fields.length, partial: c > 0 && c < fields.length };
  });

  return { perioResult, perioPct: total === 0 ? 100 : Math.round((correct / total) * 100) };
}

// Grade the user's chart ({ [tooth]: code[] }) against the case findings.
// When the case includes periodontal expectations, the final score is a 50/50
// blend of AVDC charting accuracy and periodontal probing accuracy.
export function gradeCase(dentalCase, chart, perioChart = {}) {
  const expectedMap = {};
  let expectedCount = 0;
  dentalCase.findings.forEach((f) => {
    expectedMap[f.tooth] = new Set([...(expectedMap[f.tooth] || []), ...f.symbols]);
    expectedCount += f.symbols.length;
  });

  let correct = 0;
  const findingsResult = dentalCase.findings.map((f) => {
    const have = new Set(chart[f.tooth] || []);
    const got = f.symbols.filter((s) => have.has(s));
    correct += got.length;
    return {
      ...f,
      got,
      satisfied: got.length === f.symbols.length,
      partial: got.length > 0 && got.length < f.symbols.length,
    };
  });

  const incorrect = [];
  Object.entries(chart).forEach(([tooth, codes]) => {
    const exp = expectedMap[Number(tooth)];
    codes.forEach((c) => {
      if (!exp || !exp.has(c)) incorrect.push({ tooth: Number(tooth), code: c });
    });
  });

  const missed = expectedCount - correct;
  const raw = expectedCount === 0 ? 0 : (correct - 0.5 * incorrect.length) / expectedCount;
  const avdcPct = Math.round(Math.max(0, Math.min(1, raw)) * 100);

  const { perioResult, perioPct } = gradePerio(dentalCase, perioChart);
  const pct = perioPct == null ? avdcPct : Math.round(0.5 * avdcPct + 0.5 * perioPct);

  return {
    findingsResult,
    expectedCount,
    correct,
    missed,
    incorrect,
    avdcPct,
    perioPct,
    perioResult,
    pct,
    grade: letterFor(pct),
  };
}

// How many briefing findings are fully satisfied (live progress).
export function satisfiedCount(dentalCase, chart) {
  return dentalCase.findings.filter((f) => {
    const have = new Set(chart[f.tooth] || []);
    return f.symbols.every((s) => have.has(s));
  }).length;
}
